import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { writeSelectedRecord } from '@/lib/selectedRecord';
import Layout from '@/components/layout/Layout';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ClipboardCheck, FileDown, Lock, Search, Video } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { courseHeaderLabel } from '@/lib/utils';
import { TurnusCreateButton } from '@/pages/Teaching/components/CreateTurnusDialog';
import AttendanceStatusBadge from '@/components/AttendanceStatusBadge';
import { resolveAttendanceStatus, todayIsoLocal } from '@/lib/attendanceStatus';
import { CHIP_CLASS } from '@/lib/mamvPalette';
import { matchesFreeText } from '@/components/search/SearchBuilder';

const CYCLE = { null: 'present', present: 'absent_excused', absent_excused: 'absent_unexcused', absent_unexcused: null };
const STATUS_UI = {
  present: { label: '✓', cls: CHIP_CLASS.success, title: 'Anwesend' },
  absent_excused: { label: 'E', cls: CHIP_CLASS.warning, title: 'Entschuldigt fehlend' },
  absent_unexcused: { label: 'U', cls: CHIP_CLASS.danger, title: 'Unentschuldigt fehlend' },
};

const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const nowMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function AttendanceLists() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [teachers, setTeachers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState('lists'); // 'lists' | 'stats'
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [month, setMonth] = useState(nowMonth());
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [studentQuery, setStudentQuery] = useState('');
  const [openCourses, setOpenCourses] = useState({});

  useEffect(() => {
    let cancelled = false;
    api.get('/attendance-lists/teachers', { params: { month } })
      .then((r) => {
        if (cancelled) return;
        const list = r.data.teachers || [];
        const admin = !!r.data.is_admin;
        setTeachers(list);
        setIsAdmin(admin);
        setSelected((current) => {
          const tid = searchParams.get('teacher_id');
          if (tid) {
            const hit = list.find((t) => String(t.id) === String(tid));
            if (hit) return hit;
          }
          if (current) return list.find((t) => t.id === current.id) || null;
          if (!admin && list.length === 1) return list[0];
          return null;
        });
      })
      .catch(() => toast.error('Lehrkräfte konnten nicht geladen werden'));
    return () => { cancelled = true; };
  }, [month, searchParams]);

  useEffect(() => {
    if (selected?.id) writeSelectedRecord({ teacherId: selected.id });
  }, [selected?.id]);

  useEffect(() => {
    if (view !== 'stats') return;
    api.get('/attendance-lists/stats', { params: { month } })
      .then((r) => setStats(r.data))
      .catch(() => toast.error('Auswertung konnte nicht geladen werden'));
  }, [view, month]);

  const loadMatrix = () => {
    if (!selected) { setMatrix(null); return; }
    setLoading(true);
    api.get(`/attendance-lists/teachers/${selected.id}`, { params: { month } })
      .then((r) => setMatrix(r.data))
      .catch(() => toast.error('Anwesenheitsliste konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  };

  useEffect(loadMatrix, [selected, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    return teachers.filter((t) => matchesFreeText(t, filter));
  }, [teachers, filter]);

  const mark = async (courseId, studentId, dateStr, current, currentMark) => {
    if (!matrix?.can_write) return;
    if (currentMark?.is_finalized) {
      toast.info('Finalisiert — Änderung nur mit Begründung in der Unterrichtsverwaltung');
      return;
    }
    const next = CYCLE[current ?? null];
    try {
      const r = await api.post(`/attendance-lists/courses/${courseId}/mark`, {
        lesson_date: dateStr, student_id: studentId, status: next ?? 'none',
      });
      setMatrix((m) => {
        const marks = { ...m.marks };
        const key = `${courseId}|${dateStr}|${studentId}`;
        if (r.data.status || r.data.category_code) {
          marks[key] = {
            status: r.data.status,
            notes: null,
            category_code: r.data.category_code || null,
            is_finalized: !!r.data.is_finalized,
            is_reported: !!r.data.is_reported,
          };
        } else delete marks[key];
        return {
          ...m,
          marks,
          rows: m.rows.map((row) => (row.student_id === studentId && row.course_id === courseId
            ? { ...row, prepaid: r.data.prepaid } : row)),
        };
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const finalizeMonth = async () => {
    if (!selected) return;
    if (!window.confirm('Erfasste Einträge dieses Monats finalisieren? Offene Termine bleiben offen.')) return;
    try {
      const r = await api.post(`/attendance-lists/teachers/${selected.id}/finalize`, { month });
      const n = r.data?.finalized || 0;
      toast.success(n ? `${n} Einträge finalisiert` : 'Keine erfassten Einträge zum Finalisieren');
      if (r.data?.skipped_open) toast.info(`${r.data.skipped_open} offene Termine bleiben offen`);
      loadMatrix();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Finalisieren fehlgeschlagen');
    }
  };

  const courseHeader = (row) => courseHeaderLabel(row.course_name, {
    dayOfWeek: row.day_of_week,
    startTime: row.start_time,
    isOnline: row.is_online,
  });

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/attendance-lists/teachers/${selected.id}/pdf`, {
        params: { month }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Anwesenheitsliste_${selected.name.replace(/[, ]+/g, '_')}_${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF heruntergeladen');
    } catch {
      toast.error('PDF-Export fehlgeschlagen');
    }
  };

  const toggleCourseOnline = async (row) => {
    try {
      await api.put(`/online-lessons/courses/${row.course_id}`, { is_online: !row.is_online });
      toast.success(!row.is_online ? 'Kurs auf Onlineunterricht umgestellt' : 'Onlineunterricht deaktiviert');
      loadMatrix();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Umschalten fehlgeschlagen');
    }
  };

  const toggleDateOnline = async (row, dateStr) => {
    if (!matrix?.can_write) return;
    const effective = !!matrix.online[`${row.course_id}|${dateStr}`];
    try {
      const r = await api.put(`/online-lessons/courses/${row.course_id}/dates/${dateStr}`, { is_online: !effective });
      setMatrix((m) => ({ ...m, online: { ...m.online, [`${row.course_id}|${dateStr}`]: r.data.effective_online } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Umschalten fehlgeschlagen');
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="attendance-lists-page">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="w-6 h-6 text-brand-red" />
          <h1 className="text-xl font-bold text-slate-800">Anwesenheitslisten</h1>
          {isAdmin && (
            <div className="flex gap-1 ml-4 bg-slate-100 rounded-lg p-0.5">
              {[['lists', 'Listen'], ['stats', 'Auswertung']].map(([v, l]) => (
                <button key={v} onClick={() => setView(v)}
                        data-testid={`attendance-view-${v}`}
                        className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${view === v ? 'bg-white shadow text-brand-red' : 'text-slate-500 hover:text-slate-700'}`}>
                  {l}
                </button>
              ))}
            </div>
          )}
          {view === 'stats' && (
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={() => setMonth(shiftMonth(month, -1))} className="p-1.5 rounded hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-medium w-36 text-center">{monthLabel(month)}</span>
              <button onClick={() => setMonth(shiftMonth(month, 1))} className="p-1.5 rounded hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>

        {view === 'stats' && isAdmin ? (
          <div className="space-y-4" data-testid="attendance-stats-view">
            {/* Prepaid-Warnungen */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h2 className="text-sm font-semibold text-slate-800 mb-2">Prepaid-Karten fast aufgebraucht</h2>
              {!stats ? <div className="text-xs text-slate-400">Laden...</div>
                : stats.prepaid_warnings.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Keine Karten mit ≤ 2 Rest-Einheiten 🎉</div>
                ) : (
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
                    {stats.prepaid_warnings.map((w) => (
                      <div key={`${w.student_id}-${w.card_name}`}
                           data-testid={`prepaid-warning-${w.student_id}`}
                           className={`rounded-lg border px-3 py-2 text-xs ${w.remaining === 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                        <div className="font-semibold text-slate-800">{w.student_name}</div>
                        <div className="text-slate-600">{w.card_name}: {w.used}/{w.total} verbraucht — <strong>{w.remaining} übrig</strong></div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
            {/* Fehlquoten */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h2 className="text-sm font-semibold text-slate-800 mb-2">Fehlquote pro Schüler:in ({monthLabel(month)})</h2>
              {!stats ? <div className="text-xs text-slate-400">Laden...</div>
                : stats.students.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Noch keine Anwesenheiten in diesem Monat erfasst.</div>
                ) : (
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-slate-300 text-slate-500 text-[10px] uppercase">
                        <th className="text-left py-1.5 px-2 font-medium">Schüler:in</th>
                        <th className="text-right py-1.5 px-2 font-medium w-24">Anwesend</th>
                        <th className="text-right py-1.5 px-2 font-medium w-24">Entschuldigt</th>
                        <th className="text-right py-1.5 px-2 font-medium w-28">Unentschuldigt</th>
                        <th className="text-right py-1.5 px-2 font-medium w-24">Fehlquote</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.students.map((s) => (
                        <tr key={s.student_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`stats-row-${s.student_id}`}>
                          <td className="py-1.5 px-2 font-medium">
                            <Link
                              to={`/students/${s.student_id}`}
                              className="text-slate-800 hover:text-brand-red hover:underline"
                              data-testid={`attendance-student-link-${s.student_id}`}
                            >
                              {s.name}
                            </Link>
                          </td>
                          <td className="py-1.5 px-2 text-right text-emerald-700 font-mono">{s.present}</td>
                          <td className="py-1.5 px-2 text-right text-amber-700 font-mono">{s.absent_excused}</td>
                          <td className="py-1.5 px-2 text-right text-red-700 font-mono">{s.absent_unexcused}</td>
                          <td className="py-1.5 px-2 text-right">
                            <Badge variant="outline" className={`text-[10px] py-0 px-1.5 font-mono ${s.fehlquote >= 50 ? 'border-red-300 text-red-600' : s.fehlquote > 0 ? 'border-amber-300 text-amber-700' : 'border-emerald-300 text-emerald-700'}`}>
                              {s.fehlquote}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-12 gap-4">
          {/* Lehrerliste */}
          {isAdmin && <div className="col-span-4 xl:col-span-3 bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-2 border-b border-slate-100 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={filter} onChange={(e) => setFilter(e.target.value)}
                     placeholder="Lehrkraft filtern..." className="pl-8 h-8 text-xs"
                     data-testid="attendance-teacher-filter" />
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {filtered.map((t) => (
                <button key={t.id} onClick={() => setSelected(t)}
                        data-testid={`attendance-teacher-${t.id}`}
                        className={`w-full text-left px-3 py-2 border-b border-slate-50 hover:bg-brand-red/5 transition-colors ${selected?.id === t.id ? 'bg-brand-red/10' : ''}`}>
                  <div className="text-xs font-medium text-slate-800 truncate">{t.name}</div>
                  <div className="text-[10px] text-slate-500">
                    {t.course_count} {t.course_count === 1 ? 'Kurs' : 'Kurse'} · {t.student_count} Schüler
                  </div>
                </button>
              ))}
              {filtered.length === 0 && <div className="p-4 text-xs text-slate-400 italic">Keine Lehrkräfte</div>}
            </div>
          </div>}

          {/* Gesamt-Matrix pro Lehrkraft */}
          <div className={`${isAdmin ? 'col-span-8 xl:col-span-9' : 'col-span-12'} bg-white border border-slate-200 rounded-lg`}>
            {!selected ? (
              <div className="p-10 text-center text-sm text-slate-400">Bitte links eine Lehrkraft auswählen</div>
            ) : (
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-semibold text-slate-800 text-sm" data-testid="attendance-teacher-title">{selected.name}</div>
                    <div className="text-xs text-slate-500">Unterricht in {monthLabel(month)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {matrix?.can_write && (
                      <button onClick={finalizeMonth} title="Erfasste Einträge finalisieren — offene Termine bleiben offen"
                              className="flex items-center gap-1 px-2 py-1.5 rounded border border-emerald-200 text-xs text-emerald-700 hover:bg-emerald-50"
                              data-testid="att-bulk-finalize">
                        <Lock className="w-3.5 h-3.5" /> Einträge finalisieren
                      </button>
                    )}
                    <button onClick={downloadPdf} title="Gesamtliste als PDF exportieren"
                            className="flex items-center gap-1 px-2 py-1.5 rounded border border-slate-200 text-xs text-slate-600 hover:bg-slate-50"
                            data-testid="attendance-pdf-export-btn">
                      <FileDown className="w-3.5 h-3.5" /> PDF
                    </button>
                    <button onClick={() => setMonth(shiftMonth(month, -1))} className="p-1.5 rounded hover:bg-slate-100" data-testid="attendance-prev-month"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="text-sm font-medium w-36 text-center" data-testid="attendance-month-label">{monthLabel(month)}</span>
                    <button onClick={() => setMonth(shiftMonth(month, 1))} className="p-1.5 rounded hover:bg-slate-100" data-testid="attendance-next-month"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                </div>

                {loading ? (
                  <div className="p-10 text-center text-sm text-slate-400">Laden...</div>
                ) : matrix && (matrix.rows.length === 0 ? (
                  <div className="p-10 text-center text-sm text-slate-400">Keine aktiven Belegungen bei dieser Lehrkraft</div>
                ) : (
                  <>
                    <div className="relative mb-3 max-w-sm">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <Input value={studentQuery} onChange={(e) => setStudentQuery(e.target.value)}
                             placeholder="Schüler:in suchen…" className="pl-8 h-8 text-xs"
                             data-testid="attendance-student-filter" />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="text-xs border-collapse w-full">
                        <thead>
                          <tr className="border-b border-slate-300 text-slate-500 text-[10px] uppercase">
                            <th className="text-left py-1.5 px-2 font-medium min-w-[180px]">Schüler:in</th>
                            <th className="text-left py-1.5 px-2 font-medium w-28">Prepaid</th>
                            {matrix.dates.map((d) => (
                              <th key={d} className="text-center py-1.5 px-1 font-medium w-12">
                                {d.slice(8, 10)}.{d.slice(5, 7)}.
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {matrix.rows.map((row, idx) => {
                            const prev = matrix.rows[idx - 1];
                            const newCourse = !prev || prev.course_id !== row.course_id;
                            const courseRows = matrix.rows.filter((item) => item.course_id === row.course_id);
                            const grouped = row.course_type === 'group' || courseRows.length > 1;
                            const needle = studentQuery.trim().toLowerCase();
                            const courseHit = !needle || courseRows.some((item) => (
                              String(item.name || '').toLowerCase().includes(needle)
                            ));
                            if (!courseHit) return null;
                            const expanded = !grouped || !!needle || !!openCourses[row.course_id];
                            const nameHit = !needle || String(row.name || '').toLowerCase().includes(needle);
                            if (!newCourse && (!expanded || !nameHit)) return null;
                            return (
                              <React.Fragment key={`${row.course_id}-${row.student_id}`}>
                                {newCourse && (
                                  <tr className="bg-brand-red/5 border-b border-brand-red/10" data-testid={`course-section-${row.course_id}`}>
                                    <td colSpan={2} className="py-1 px-2 text-[11px] font-semibold text-brand-red">
                                      <div className="flex items-center gap-2">
                                        {grouped && (
                                          <button
                                            type="button"
                                            aria-expanded={expanded}
                                            data-testid={`course-toggle-${row.course_id}`}
                                            className="rounded p-0.5 text-brand-red hover:bg-brand-red/10"
                                            onClick={() => setOpenCourses((prevOpen) => ({
                                              ...prevOpen,
                                              [row.course_id]: !prevOpen[row.course_id],
                                            }))}
                                          >
                                            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                                          </button>
                                        )}
                                        <span>{courseHeader(row)}</span>
                                        {row.is_online && (
                                          <span className="inline-flex items-center gap-0.5 rounded bg-emerald-100 border border-emerald-300 text-emerald-700 text-[9px] font-bold px-1 py-0" data-testid={`online-badge-${row.course_id}`}>
                                            <Video className="w-2.5 h-2.5" /> ONLINE
                                          </span>
                                        )}
                                        {matrix.can_write && (
                                          <TurnusCreateButton
                                            courseId={row.course_id}
                                            visible={row.day_of_week == null && row.dates.length > 0}
                                            onSuccess={loadMatrix}
                                            testId={`turnus-create-${row.course_id}`}
                                          />
                                        )}
                                        {matrix.can_write && (
                                          <button onClick={() => toggleCourseOnline(row)}
                                                  title={row.is_online ? 'Onlineunterricht für diesen Kurs deaktivieren' : 'Kurs auf Onlineunterricht umstellen'}
                                                  data-testid={`course-online-toggle-${row.course_id}`}
                                                  className={`px-1.5 py-0.5 rounded border text-[9px] font-semibold transition-colors ${row.is_online ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-slate-300 text-slate-500 hover:border-brand-red/40 hover:text-brand-red'}`}>
                                            {row.is_online ? 'Online aus' : 'Online an'}
                                          </button>
                                        )}
                                        {(row.is_online || row.dates.some((d) => matrix.online?.[`${row.course_id}|${d}`])) && (
                                          <button onClick={() => navigate(`/online/${row.course_id}`)}
                                                  data-testid={`join-online-${row.course_id}`}
                                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-brand-red text-white text-[9px] font-semibold hover:bg-brand-red/90 transition-colors">
                                            <Video className="w-2.5 h-2.5" /> Beitreten
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                    {matrix.dates.map((d) => {
                                      if (!row.dates.includes(d)) return <td key={d} />;
                                      const isOnline = !!matrix.online?.[`${row.course_id}|${d}`];
                                      return (
                                        <td key={d} className="text-center py-0.5 px-1">
                                          <button onClick={() => toggleDateOnline(row, d)}
                                                  disabled={!matrix.can_write}
                                                  title={isOnline ? 'Termin ist ONLINE — klicken für Präsenz' : 'Diesen Termin online schalten'}
                                                  data-testid={`date-online-toggle-${row.course_id}-${d}`}
                                                  className={`w-5 h-5 mx-auto flex items-center justify-center rounded transition-colors ${isOnline ? 'bg-emerald-100 text-emerald-600' : 'text-slate-300 hover:text-brand-red'} ${matrix.can_write ? 'cursor-pointer' : 'cursor-default'}`}>
                                            <Video className="w-3 h-3" />
                                          </button>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                )}
                                {expanded && nameHit && <tr className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="py-1.5 px-2 font-medium">
                                    <Link
                                      to={`/students/${row.student_id}`}
                                      className="text-slate-800 hover:text-brand-red hover:underline"
                                      data-testid={`attendance-student-link-${row.student_id}`}
                                    >
                                      {row.name}
                                    </Link>
                                  </td>
                                  <td className="py-1.5 px-2">
                                    {row.prepaid ? (
                                      <Badge variant="outline"
                                             className={`text-[10px] py-0 px-1.5 ${row.prepaid.used >= row.prepaid.total ? 'border-red-300 text-red-600' : 'border-brand-red/30 text-brand-red'}`}
                                             data-testid={`prepaid-badge-${row.student_id}`}>
                                        {row.prepaid.used}/{row.prepaid.total}
                                        {row.prepaid.remaining != null ? ` · ${row.prepaid.remaining} übrig` : ''}
                                      </Badge>
                                    ) : <span className="text-slate-300">—</span>}
                                  </td>
                                  {matrix.dates.map((d) => {
                                    if (!row.dates.includes(d)) {
                                      return <td key={d} className="text-center py-1 px-1"><div className="w-8 h-8 rounded bg-slate-50 mx-auto" /></td>;
                                    }
                                    const m = matrix.marks[`${row.course_id}|${d}|${row.student_id}`];
                                    const locked = !!m?.is_finalized;
                                    const resolved = resolveAttendanceStatus({
                                      category_code: m?.category_code,
                                      status: m?.status,
                                      lesson_date: d,
                                      today: todayIsoLocal(),
                                    });
                                    const ui = !resolved && m ? STATUS_UI[m.status] : null;
                                    return (
                                      <td key={d} className="text-center py-1 px-1">
                                        <button
                                          onClick={() => mark(row.course_id, row.student_id, d, m?.status ?? null, m)}
                                          disabled={!matrix.can_write || locked}
                                          title={locked ? 'Finalisiert — nicht per Klick änderbar' : (resolved?.title || ui?.title || 'Klicken zum Markieren')}
                                          data-testid={`attendance-cell-${row.course_id}-${row.student_id}-${d}`}
                                          className={`w-8 h-8 rounded border text-xs font-bold transition-colors inline-flex items-center justify-center ${resolved ? 'bg-transparent border-transparent p-0' : ui ? ui.cls : 'bg-white border-slate-200 text-slate-300 hover:border-brand-anthracite/40'} ${matrix.can_write && !locked ? 'cursor-pointer' : 'cursor-default'} ${locked ? 'opacity-80' : ''}`}>
                                          {resolved ? (
                                            <AttendanceStatusBadge
                                              status={resolved}
                                              size="sm"
                                              testId={`attendance-status-${row.course_id}-${row.student_id}-${d}`}
                                            />
                                          ) : (ui?.label || '')}
                                        </button>
                                      </td>
                                    );
                                  })}
                                </tr>}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-500 flex-wrap">
                      <span>✓ Unterricht erteilt</span>
                      <span>EA entschuldigt</span>
                      <span>UA unentschuldigt</span>
                      <span>LA Lehrkraft abwesend</span>
                      <span>→ verlegt</span>
                      <span>? offen</span>
                      <span>finalisiert: nicht mehr per Klick änderbar</span>
                      <span className="text-slate-400">Graue Felder: kein Termin an diesem Tag · Prepaid: ✓ und U verbrauchen eine Einheit</span>
                      <span className="inline-flex items-center gap-1 text-emerald-600"><Video className="w-3 h-3" /> Kamera-Symbol in der Kurszeile: Termin online schalten</span>
                      {!matrix.can_write && <Badge variant="outline" className="text-[10px]">Nur Lesezugriff</Badge>}
                    </div>
                  </>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </Layout>
  );
}
